import React, { useState, useEffect } from 'react';
import {
  Check,
  Palette,
  Sparkles,
  Bold,
  Crown,
  Heart,
  Moon,
  Brush,
  Minus,
  Eye,
} from 'lucide-react';
import { themesApi } from '../api/client';
import { cn } from '../utils/cn';
import { ThemePreviewModal } from './ThemePreviewModal';
import { BrandPackagePreview } from './BrandPackageManager/BrandPackagePreview';
import type { BrandPackageColors, BrandPackageFonts } from '../api/modules/brandPackages';

interface Theme {
  id: string;
  name: string;
  description: string;
  preview: string | null;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    muted: string;
  };
  typography: {
    headingFont: string;
    bodyFont: string;
    baseSize: string;
  };
  features: string[];
}

interface ThemeSelectorProps {
  selectedTheme: string;
  onThemeSelect: (themeId: string) => void;
}

const themeIcons: Record<string, React.ReactNode> = {
  standard: <Palette className="w-5 h-5" />,
  modern: <Sparkles className="w-5 h-5" />,
  bold: <Bold className="w-5 h-5" />,
  elegant: <Crown className="w-5 h-5" />,
  playful: <Heart className="w-5 h-5" />,
  dark: <Moon className="w-5 h-5" />,
  gradient: <Brush className="w-5 h-5" />,
  minimal: <Minus className="w-5 h-5" />,
};

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ selectedTheme, onThemeSelect }) => {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewingTheme, setPreviewingTheme] = useState<Theme | null>(null);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      setIsLoading(true);
      const response = await themesApi.list();

      console.log('🎨 ThemeSelector: API response:', response);

      // Handle different response formats from the API
      let themesArray: Theme[] = [];
      if (Array.isArray(response)) {
        themesArray = response;
      } else if (response && typeof response === 'object') {
        // Check for common API response wrappers
        const apiResponse = response as any;
        if (Array.isArray(apiResponse.data)) {
          themesArray = apiResponse.data;
        } else if (Array.isArray(apiResponse.themes)) {
          themesArray = apiResponse.themes;
        }
      }

      console.log('🎨 ThemeSelector: Parsed themes array:', themesArray);
      console.log('🎨 ThemeSelector: First theme structure:', themesArray[0]);

      setThemes(themesArray);
    } catch (err) {
      console.error('Failed to load themes:', err);
      setError('Failed to load themes. Using default theme.');
      // Set default theme if loading fails
      setThemes([
        {
          id: 'standard',
          name: 'Standard',
          description: 'Classic professional design',
          preview: null,
          colors: {
            primary: '#4F46E5',
            secondary: '#10B981',
            accent: '#F59E0B',
            background: '#FFFFFF',
            text: '#1F2937',
            muted: '#6B7280',
          },
          typography: {
            headingFont: 'Inter, system-ui, sans-serif',
            bodyFont: 'Inter, system-ui, sans-serif',
            baseSize: '16px',
          },
          features: ['Clean Layout', 'Professional Typography', 'Balanced Spacing'],
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Transform theme data to BrandPackagePreview-compatible format
   */
  const getThemePreviewData = (
    theme: Theme
  ): { colors: BrandPackageColors; fonts: BrandPackageFonts } => {
    const customColors = (theme as any).customColors as BrandPackageColors | undefined;

    // Build colors from theme's customColors or fall back to defaults
    const colors: BrandPackageColors = customColors || {
      navbar: { background: '#1f2937', text: '#ffffff' },
      hero: { background: theme.colors?.primary || '#4F46E5', text: '#ffffff' },
      features: {
        background: theme.colors?.background || '#ffffff',
        iconColor: theme.colors?.secondary || '#10B981',
      },
      footer: { background: '#1f2937', text: '#ffffff' },
      cta: { buttonBg: theme.colors?.primary || '#4F46E5' },
      testimonials: { starColor: theme.colors?.accent || '#F59E0B' },
    };

    // Build fonts from theme's typography
    const fonts: BrandPackageFonts = {
      headingFont: theme.typography?.headingFont?.split(',')[0]?.trim() || 'Inter',
      bodyFont: theme.typography?.bodyFont?.split(',')[0]?.trim() || 'Inter',
      baseSize: theme.typography?.baseSize || '16px',
    };

    return { colors, fonts };
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 mx-auto mb-2 spinner"></div>
        <p className="text-gray-600">Loading themes...</p>
      </div>
    );
  }

  if (error && themes.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose a Theme</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {themes.map(theme => (
          <div
            key={theme.id}
            onClick={() => onThemeSelect(theme.id)}
            className={cn(
              'relative group rounded-lg border p-4 cursor-pointer transition-all',
              selectedTheme === theme.id
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
            )}
          >
            {/* Selection checkmark */}
            {selectedTheme === theme.id && (
              <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1 z-10">
                <Check className="h-3 w-3" />
              </div>
            )}

            {/* Preview button (top-left, visible on hover) */}
            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setPreviewingTheme(theme);
                }}
                className="p-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-300 shadow-sm"
                title="Preview theme"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Theme Preview Widget */}
            {(() => {
              const { colors, fonts } = getThemePreviewData(theme);
              return <BrandPackagePreview colors={colors} fonts={fonts} size="medium" />;
            })()}

            {/* Theme name below the widget */}
            <div className="mt-3">
              <h4 className="font-medium text-gray-900 truncate">{theme.name}</h4>
              {theme.description && (
                <p className="text-sm text-gray-500 truncate mt-1">{theme.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Selected theme details */}
      {selectedTheme && themes.length > 0 && (
        <div className="mt-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
          <div className="flex items-center space-x-2 text-primary-800">
            {themeIcons[selectedTheme]}
            <span className="font-medium">
              {themes.find(t => t.id === selectedTheme)?.name} Theme Selected
            </span>
          </div>
          <p className="text-sm text-primary-600 mt-1">
            {themes.find(t => t.id === selectedTheme)?.description}
          </p>
        </div>
      )}

      {/* Theme Preview Modal */}
      {previewingTheme && (
        <ThemePreviewModal
          isOpen={!!previewingTheme}
          onClose={() => setPreviewingTheme(null)}
          themeId={previewingTheme.id}
          themeName={previewingTheme.name}
          customColors={(previewingTheme as any).customColors}
          onSelectTheme={onThemeSelect}
        />
      )}
    </div>
  );
};
